import React, { useState, useEffect } from 'react';
import {
  CurrencyYenIcon,
  ArrowLeftIcon,
  EyeIcon,
  CheckCircleIcon,
  XMarkIcon,
  CalendarIcon,
  ClockIcon,
  CreditCardIcon,
  ExclamationCircleIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { API_BASE } from '../../lib/config';

const QuickPaymentErrors = ({ onBack }) => {
  const [paymentErrors, setPaymentErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorTypeFilter, setErrorTypeFilter] = useState('all');
  const [stats, setStats] = useState({
    errorPayments: 0,
    todayErrorCount: 0,
    resolvedErrorCount: 0,
    totalErrorAmount: 0
  });

  useEffect(() => {
    fetchPaymentErrors();
    fetchStats();
  }, [errorTypeFilter]);

  const fetchPaymentErrors = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/admin/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        let data = await response.json();
        // Filter for errors
        const failed = data.filter(p => p.status === 'failed' || p.status === 'error');
        setPaymentErrors(failed);
      } else {
        setPaymentErrors([]);
      }

    } catch (error) {
      console.error('Error fetching payment errors:', error);
      toast.error('決済エラー情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/admin/payments/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setStats(await res.json());
      }
    } catch (e) {
      console.error('Error fetching payment stats:', e);
    }
  };

  const handleErrorAction = async (errorId, action) => {
    try {
      // 実際の実装ではAPI呼び出し
      const actionLabels = {
        retry: '再試行',
        resolve: '解決',
        contact_user: 'ユーザーに連絡'
      };

      toast.success(`${actionLabels[action]}を実行しました`);
      fetchPaymentErrors(); // データを再取得
      fetchStats(); // 統計も更新
    } catch (error) {
      toast.error('操作に失敗しました');
    }
  };

  const getErrorTypeBadge = (errorType) => {
    const types = {
      card_declined: { class: 'bg-red-100 text-red-800', label: 'カード拒否' },
      insufficient_funds: { class: 'bg-yellow-100 text-yellow-800', label: '残高不足' },
      expired_card: { class: 'bg-orange-100 text-orange-800', label: 'カード期限切れ' },
      network_error: { class: 'bg-purple-100 text-purple-800', label: 'ネットワークエラー' },
      system_error: { class: 'bg-gray-100 text-gray-800', label: 'システムエラー' }
    };
    const badge = types[errorType] || { class: 'bg-gray-100 text-gray-800', label: 'その他' };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.class}`}>
        {badge.label}
      </span>
    );
  };

  const getPaymentMethodBadge = (method) => {
    const methods = {
      credit_card: { class: 'bg-blue-100 text-blue-800', label: 'クレジットカード' },
      bank_transfer: { class: 'bg-green-100 text-green-800', label: '銀行振込' },
      digital_wallet: { class: 'bg-purple-100 text-purple-800', label: 'デジタルウォレット' }
    };
    const badge = methods[method] || { class: 'bg-gray-100 text-gray-800', label: 'その他' };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.class}`}>
        {badge.label}
      </span>
    );
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* 戻るボタン */}
      {onBack && (
        <div className="flex items-center">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            ダッシュボードに戻る
          </button>
        </div>
      )}

      {/* ヘッダー */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <CurrencyYenIcon className="w-8 h-8 text-purple-600 mr-3" />
            決済エラー確認
          </h2>
          <p className="text-gray-600 mt-1">決済処理中に発生したエラーの確認・対応</p>
        </div>

        {/* エラータイプフィルター */}
        <div className="flex items-center space-x-3">
          <label className="text-sm font-medium text-gray-700">エラータイプ:</label>
          <select
            value={errorTypeFilter}
            onChange={(e) => setErrorTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">すべて</option>
            <option value="card_declined">カード拒否</option>
            <option value="insufficient_funds">残高不足</option>
            <option value="expired_card">カード期限切れ</option>
            <option value="network_error">ネットワークエラー</option>
            <option value="system_error">システムエラー</option>
          </select>
        </div>
      </div>

      {/* 統計カード */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <ExclamationCircleIcon className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">未解決エラー</p>
              <p className="text-2xl font-bold text-gray-900">{stats.errorPayments}件</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <ClockIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">今日のエラー</p>
              <p className="text-2xl font-bold text-gray-900">{stats.todayErrorCount}件</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">解決済み</p>
              <p className="text-2xl font-bold text-gray-900">{stats.resolvedErrorCount}件</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <CreditCardIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">エラー総額</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalErrorAmount.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* エラー一覧 */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium">決済エラー一覧</h3>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-500">読み込み中...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    決済情報
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ユーザー
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    エラー詳細
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    決済方法
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    再試行回数
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    発生日時
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paymentErrors.map((error) => (
                  <tr key={error.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {error.amount.toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-500">
                          {error.job_title}
                        </div>
                        <div className="text-xs text-gray-400">
                          ID: {error.payment_id}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                          <UserIcon className="w-4 h-4 text-gray-600" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{error.user_name}</div>
                          <div className="text-xs text-gray-500">
                            {error.user_type === 'job_seeker' ? '求職者' : '事業者'}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        {getErrorTypeBadge(error.error_type)}
                        <div className="text-sm text-gray-500 max-w-xs truncate">
                          {error.error_message}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPaymentMethodBadge(error.payment_method)}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{error.retry_count}回</div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(error.occurred_at)}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => alert(`決済エラーID: ${error.id}の詳細を表示`)}
                          className="text-blue-600 hover:text-blue-900 flex items-center"
                        >
                          <EyeIcon className="w-4 h-4 mr-1" />
                          詳細
                        </button>
                        <button
                          onClick={() => handleErrorAction(error.id, 'retry')}
                          className="text-green-600 hover:text-green-900 flex items-center"
                        >
                          <CheckCircleIcon className="w-4 h-4 mr-1" />
                          再試行
                        </button>
                        <button
                          onClick={() => handleErrorAction(error.id, 'contact_user')}
                          className="text-purple-600 hover:text-purple-900 flex items-center"
                        >
                          <UserIcon className="w-4 h-4 mr-1" />
                          連絡
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {paymentErrors.length === 0 && !loading && (
          <div className="p-8 text-center">
            <CurrencyYenIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">該当する決済エラーがありません</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickPaymentErrors;